import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import "./Questions.css";
import { createSubmittedQuestion } from "../../../../api/questions";
import { scrollToElementById } from "../../../../utils/scrollToId";

const QUESTION_TEXT_MAX = 8000;

const Questions = () => {
  const location = useLocation();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [form, setForm] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    location: "",
    questionText: "",
  });

  useEffect(() => {
    if (location.hash !== "#questions-contact-form") return;
    const frame = window.requestAnimationFrame(() => {
      scrollToElementById("questions-contact-form");
    });
    return () => window.cancelAnimationFrame(frame);
  }, [location.pathname, location.hash]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!String(form.name || "").trim()) {
      setError("Name is required.");
      return;
    }
    if (!String(form.email || "").trim()) {
      setError("Email is required.");
      return;
    }
    if (!String(form.phoneNumber || "").trim()) {
      setError("Phone number is required.");
      return;
    }
    if (!String(form.location || "").trim()) {
      setError("Location is required.");
      return;
    }
    const questionText = String(form.questionText || "").trim();
    if (!questionText) {
      setError("Question is required.");
      return;
    }
    if (questionText.length > QUESTION_TEXT_MAX) {
      setError(`Question must be at most ${QUESTION_TEXT_MAX} characters.`);
      return;
    }

    setSubmitting(true);
    try {
      await createSubmittedQuestion({
        name: String(form.name || "").trim(),
        email: String(form.email || "").trim(),
        phoneNumber: String(form.phoneNumber || "").trim(),
        location: String(form.location || "").trim(),
        questionText,
      });
      setSuccess("Your question has been sent successfully.");
      setForm({
        name: "",
        email: "",
        phoneNumber: "",
        location: "",
        questionText: "",
      });
    } catch (submitError) {
      setError(
        submitError?.response?.data?.message ||
          submitError?.message ||
          "Failed to send your question."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const questionLen = String(form.questionText || "").length;

  return (
    <section id="questions-contact-form" className="questionsContact" aria-label="Questions">
      <div className="questionsContact-inner">
        <div className="questionsContact-left">
          <span className="questionsContact-tag">QUESTIONS</span>

          <h2 className="questionsContact-title">
            Do you have <span>more question?</span>
          </h2>

          <p className="questionsContact-text">
            Whether you need more information about our products, services, or project support, our team is here to
            help. Fill out the form and we’ll get back to you as soon as possible with the answers and guidance you
            need.
          </p>
        </div>

        <div className="questionsContact-right">
          <h3 className="questionsContact-formTitle">Ask a question</h3>

          <form className="questionsContact-form" onSubmit={handleSubmit} noValidate>
            <div className="questionsContact-row questionsContact-rowHalf">
              <input
                type="text"
                placeholder="Your name"
                aria-label="Your name"
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                autoComplete="name"
              />
              <input
                type="email"
                placeholder="Your email"
                aria-label="Your email"
                value={form.email}
                onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                autoComplete="email"
              />
            </div>

            <div className="questionsContact-row">
              <input
                type="tel"
                placeholder="Phone number"
                aria-label="Phone number"
                value={form.phoneNumber}
                onChange={(e) => setForm((p) => ({ ...p, phoneNumber: e.target.value }))}
                autoComplete="tel"
              />
            </div>

            <div className="questionsContact-row">
              <input
                type="text"
                placeholder="Location"
                aria-label="Location"
                value={form.location}
                onChange={(e) => setForm((p) => ({ ...p, location: e.target.value }))}
                autoComplete="address-level2"
              />
            </div>

            <div className="questionsContact-row questionsContact-rowTextarea">
              <textarea
                id="questions-question-text"
                placeholder="Describe your question (required)"
                aria-label="Your question"
                value={form.questionText}
                maxLength={QUESTION_TEXT_MAX}
                onChange={(e) => setForm((p) => ({ ...p, questionText: e.target.value }))}
                rows={6}
              />
              <span className="questionsContact-charCount" aria-live="polite">
                {questionLen} / {QUESTION_TEXT_MAX}
              </span>
            </div>

            {error ? <p className="questionsContact-message questionsContact-messageError">{error}</p> : null}
            {success ? <p className="questionsContact-message questionsContact-messageSuccess">{success}</p> : null}

            <div className="questionsContact-action">
              <button type="submit" className="questionsContact-submit" disabled={submitting}>
                {submitting ? "Submitting..." : "Submit question"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Questions;
